import axios from "axios";
import { fbAuth } from "../services/firebaseService/connection";
import { getIdToken } from "firebase/auth";
import logger from "./logger";

/**
 * ChantAPI - A singleton API client that can be used anywhere (React components, JS modules, services)
 * Automatically fetches the current user's token from Firebase Auth for each request
 *
 * @example
 * // In any file (React component, service, utility, etc.)
 * import { chantApi } from '../utils/chantApi';
 *
 * const data = await chantApi.post('/api/endpoint', { key: 'value' });
 * const users = await chantApi.get('/api/users');
 */

/**
 * Gets the current authentication token from Firebase Auth
 * @returns {Promise<string|null>} The authentication token or null if not authenticated
 */
const getCurrentToken = async () => {
  try {
    if (!fbAuth.currentUser) {
      logger.warn("No authenticated user found");
      return null;
    }
    return await getIdToken(fbAuth.currentUser);
  } catch (error) {
    logger.error("Failed to get authentication token:", error);
    return null;
  }
};

/**
 * Creates headers with authentication token
 * @param {Object} customHeaders - Additional headers to merge
 * @returns {Promise<Object>} Headers object with Authorization
 */
const createHeaders = async (customHeaders = {}) => {
  const token = await getCurrentToken();

  if (!token) {
    throw new Error("Authentication required. Please log in.");
  }

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...customHeaders,
  };
};

/**
 * ChantAPI - Singleton API client
 * Can be imported and used anywhere in the codebase
 */
export const chantApi = {
  /**
   * Make a POST request
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request body data
   * @param {Object} config - Additional axios config (optional)
   * @returns {Promise} Axios response promise
   *
   * @example
   * const response = await chantApi.post('/api/gallery/images', {
   *   groupId: 'abc123',
   *   limit: 30
   * });
   */
  post: async (url, data, config = {}) => {
    try {
      const headers = await createHeaders(config.headers);
      return await axios.post(url, data, {
        ...config,
        headers,
      });
    } catch (error) {
      logger.error(`POST request failed for ${url}:`, error);
      throw error;
    }
  },

  /**
   * Make a GET request
   * @param {string} url - The endpoint URL
   * @param {Object} config - Additional axios config (optional)
   * @returns {Promise} Axios response promise
   *
   * @example
   * const response = await chantApi.get('/api/users');
   */
  get: async (url, config = {}) => {
    try {
      const headers = await createHeaders(config.headers);
      return await axios.get(url, {
        ...config,
        headers,
      });
    } catch (error) {
      logger.error(`GET request failed for ${url}:`, error);
      throw error;
    }
  },

  /**
   * Make a PUT request
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request body data
   * @param {Object} config - Additional axios config (optional)
   * @returns {Promise} Axios response promise
   *
   * @example
   * const response = await chantApi.put('/api/user/123', {
   *   name: 'Updated Name'
   * });
   */
  put: async (url, data, config = {}) => {
    try {
      const headers = await createHeaders(config.headers);
      return await axios.put(url, data, {
        ...config,
        headers,
      });
    } catch (error) {
      logger.error(`PUT request failed for ${url}:`, error);
      throw error;
    }
  },

  /**
   * Make a PATCH request
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request body data
   * @param {Object} config - Additional axios config (optional)
   * @returns {Promise} Axios response promise
   *
   * @example
   * const response = await chantApi.patch('/api/user/123', {
   *   status: 'active'
   * });
   */
  patch: async (url, data, config = {}) => {
    try {
      const headers = await createHeaders(config.headers);
      return await axios.patch(url, data, {
        ...config,
        headers,
      });
    } catch (error) {
      logger.error(`PATCH request failed for ${url}:`, error);
      throw error;
    }
  },

  /**
   * Make a DELETE request
   * @param {string} url - The endpoint URL
   * @param {Object} config - Additional axios config (optional)
   * @returns {Promise} Axios response promise
   *
   * @example
   * const response = await chantApi.delete('/api/image/456');
   */
  delete: async (url, config = {}) => {
    try {
      const headers = await createHeaders(config.headers);
      return await axios.delete(url, {
        ...config,
        headers,
      });
    } catch (error) {
      logger.error(`DELETE request failed for ${url}:`, error);
      throw error;
    }
  },
};

/**
 * USAGE EXAMPLES:
 *
 * ==========================================
 * 1. IN REACT COMPONENTS:
 * ==========================================
 * import { chantApi } from '../utils/chantApi';
 *
 * const MyComponent = () => {
 *   const fetchData = async () => {
 *     const response = await chantApi.get('/api/users');
 *   };
 *
 *   return <button onClick={fetchData}>Load</button>;
 * };
 *
 * ==========================================
 * 2. IN SERVICE FILES (Plain JS):
 * ==========================================
 * import { chantApi } from '../../utils/chantApi';
 *
 * export const fetchGalleryImages = async (groupId) => {
 *   const response = await chantApi.post('/api/gallery', {
 *     groupId,
 *     limit: 30
 *   });
 *   return response.data;
 * };
 *
 * ==========================================
 * 3. IN UTILITY FUNCTIONS:
 * ==========================================
 * import { chantApi } from '../utils/chantApi';
 *
 * export const deleteImage = async (imageId) => {
 *   return chantApi.delete(`/api/images/${imageId}`);
 * };
 *
 * ==========================================
 * 4. WITH REACT QUERY:
 * ==========================================
 * import { useQuery } from '@tanstack/react-query';
 * import { chantApi } from '../utils/chantApi';
 *
 * const { data } = useQuery({
 *   queryKey: ['users'],
 *   queryFn: () => chantApi.get('/api/users')
 * });
 *
 * ==========================================
 * 5. WITH CUSTOM HEADERS:
 * ==========================================
 * const response = await chantApi.post('/api/endpoint', data, {
 *   headers: {
 *     'X-Custom-Header': 'value'
 *   }
 * });
 */
